import { useCallback, useState } from 'react'
import req from '@sportninja/common/api/request'

/**
 * Single AI Suggest suggestion as shaped by AiSuggestResource (api2 →
 * scheduler-python). The frontend treats `parameters` as opaque until the
 * Edit-params flow is opened, where they're rendered per `type_code`.
 */
export interface AiSuggestion {
  id: string
  status:
    | 'pending'
    | 'accepted'
    | 'accepted_edited'
    | 'rejected'
    | 'ignored'
    | 'expired'
  type_code: string
  is_hard: boolean | null
  priority: number | null
  weight: number | null
  division_uids: string[] | null
  team_uids: string[] | null
  parameters: Record<string, unknown>
  confidence: number | null
  rationale: string | null
  evidence: Record<string, unknown>
  policy_org_uid: string | null
  policy_version: string | null
  re_ranker_action: 'none' | 'filtered' | 'demoted' | 'boosted'
  re_ranker_touched: boolean
  expires_at: string | null
  responded_at: string | null
  resulting_constraint_id: string | null
  created_at: string | null
}

export interface AiSuggestMeta {
  policy_org_uid: string | null
  policy_version: string | null
  re_ranker_modifications: number
  generated_at: string | null
  count: number
}

interface UseAiSuggestOptions {
  flightId: string | null
}

interface UseAiSuggestResult {
  suggestions: AiSuggestion[]
  meta: AiSuggestMeta | null
  isLoading: boolean
  error: Error | null
  errorStatus: number | null
  /**
   * Trigger a fresh suggest call to api2. Persists the suggestions on the
   * backend and returns them. Intended to be called when the modal opens.
   */
  fetchSuggestions: () => Promise<AiSuggestion[]>
  /**
   * Optimistically remove a suggestion from local state after the user
   * accepts or rejects it (so the modal collapses the row immediately).
   */
  removeLocally: (suggestionId: string) => void
}

/**
 * Hook that requests fresh AI Suggest constraints from api2 and exposes
 * the resulting list plus per-request meta. Errors are surfaced with
 * their HTTP status so the modal can render a friendly toast on the
 * pilot-org gate (422) without falling through to the generic error UI.
 */
export const useAiSuggest = ({
  flightId,
}: UseAiSuggestOptions): UseAiSuggestResult => {
  'use memo'

  const [suggestions, setSuggestions] = useState<AiSuggestion[]>([])
  const [meta, setMeta] = useState<AiSuggestMeta | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)
  const [errorStatus, setErrorStatus] = useState<number | null>(null)

  const fetchSuggestions = useCallback(async (): Promise<AiSuggestion[]> => {
    if (!flightId) {
      return []
    }

    setIsLoading(true)
    setError(null)
    setErrorStatus(null)

    try {
      const response = await req(
        `/scheduler/flights/${flightId}/ai-suggest-constraints`,
        { method: 'POST' }
      )

      const list: AiSuggestion[] = response?.data ?? []
      const responseMeta: AiSuggestMeta = response?.meta ?? {
        policy_org_uid: null,
        policy_version: null,
        re_ranker_modifications: 0,
        generated_at: null,
        count: list.length,
      }

      setSuggestions(list)
      setMeta(responseMeta)
      return list
    } catch (err: unknown) {
      const status =
        err && typeof err === 'object' && 'status' in err
          ? (err as { status?: number }).status ?? null
          : null
      setErrorStatus(status)
      const realError =
        err instanceof Error ? err : new Error('Failed to fetch AI suggestions')
      setError(realError)
      setSuggestions([])
      setMeta(null)
      throw realError
    } finally {
      setIsLoading(false)
    }
  }, [flightId])

  const removeLocally = useCallback((suggestionId: string) => {
    setSuggestions((prev) => prev.filter((s) => s.id !== suggestionId))
  }, [])

  return {
    suggestions,
    meta,
    isLoading,
    error,
    errorStatus,
    fetchSuggestions,
    removeLocally,
  }
}
